import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useRouter } from 'src/routes/hooks';

import { useLocalStorage } from 'src/hooks/use-local-storage';
import { useAuthContext } from 'src/auth/hooks';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { CompleteProfileModal } from 'src/components/complete-profile-modal';

// ----------------------------------------------------------------------

const ONBOARDING_STEPS = [
    {
        id: 'account',
        title: 'Complete seus dados da conta',
        description: 'Preencha as informações necessárias para finalizar seu cadastro.',
        icon: 'solar:user-id-bold',
        path: '/dashboard/user/account',
    },
    {
        id: 'professional',
        title: 'Cadastre o profissional',
        description: 'Adicione os profissionais que atendem no seu negócio.',
        icon: 'solar:users-group-rounded-bold',
        path: '/dashboard/manage_partner_professionals',
    },
    {
        id: 'services',
        title: 'Adicione seus serviços',
        description: 'Informe os serviços, duração, preço e profissional responsável.',
        icon: 'solar:clipboard-list-bold',
        path: '/dashboard/establishment-packages-services',
    },
    {
        id: 'page',
        title: 'Configure sua página',
        description: 'Personalize a página do seu negócio com informações e imagens.',
        icon: 'solar:settings-bold',
        path: '/dashboard/portfolio',
    },
    {
        id: 'link',
        title: 'Copie o link da sua página',
        description: 'Use esse link para compartilhar na sua rede social e com seus clientes.',
        icon: 'solar:link-bold',
        path: null, // Ação especial - copiar link
    },
];

const STORAGE_KEY = 'onboarding-steps-completed';
const STORAGE_KEY_DISMISSED = 'onboarding-dismissed';

export const ONBOARDING_DRAWER_WIDTH = 380;

// ----------------------------------------------------------------------

export function OnboardingDrawer({ autoOpen = false, onOpenChange }) {
    const theme = useTheme();
    const router = useRouter();
    const { establishment } = useAuthContext();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Guarda quais passos foram completados { account: true, professional: false, ... }
    const { state: completedSteps, setState: setCompletedSteps } = useLocalStorage(STORAGE_KEY, {});

    // Guarda se o usuário já finalizou o onboarding (clicou em "Tudo Pronto!")
    const { state: onboardingDismissed, setState: setOnboardingDismissed } = useLocalStorage(STORAGE_KEY_DISMISSED, false);

    // Verifica diretamente do localStorage para evitar race condition
    const [isInitialized, setIsInitialized] = useState(false);
    const [open, setOpen] = useState(false);
    const [openCompleteProfileModal, setOpenCompleteProfileModal] = useState(false);
    const [professionals, setProfessionals] = useState([]);
    const [services, setServices] = useState([]);

    // Busca os profissionais do estabelecimento
    const fetchProfessionals = useCallback(() => {
        if (establishment?.id) {
            fetch(`http://localhost:8080/api/profissionais-estabelecimento/${establishment.id}`)
                .then((response) => response.ok ? response.json() : [])
                .then((data) => setProfessionals(Array.isArray(data) ? data : []))
                .catch(() => setProfessionals([]));
        }
    }, [establishment?.id]);

    // Busca os serviços do estabelecimento
    const fetchServices = useCallback(() => {
        if (establishment?.id) {
            fetch(`http://localhost:8080/api/estabelecimento/${establishment.id}/servicos`)
                .then((response) => response.ok ? response.json() : [])
                .then((data) => setServices(Array.isArray(data) ? data : []))
                .catch(() => setServices([]));
        }
    }, [establishment?.id]);

    // Busca inicial
    useEffect(() => {
        fetchProfessionals();
        fetchServices();
    }, [fetchProfessionals, fetchServices]);

    // Atualiza a cada 5 segundos enquanto o drawer estiver aberto
    useEffect(() => {
        if (!open) return undefined;

        const interval = setInterval(() => {
            fetchProfessionals();
            fetchServices();
        }, 5000);

        return () => clearInterval(interval);
    }, [open, fetchProfessionals, fetchServices]);

    // Verifica se um passo está realmente completo (com regras especiais)
    const isStepCompleted = useCallback((stepId) => {
        // Para o passo 'account', só está completo se o CPF for diferente de 15002434690
        if (stepId === 'account') {
            const cpf = establishment?.cpf || establishment?.cnpj || '';
            if (cpf === '15002434690') {
                return false;
            }
        }
        // Para o passo 'professional', só está completo se houver pelo menos 1 profissional
        if (stepId === 'professional') {
            if (professionals.length === 0) {
                return false;
            }
        }
        // Para o passo 'services', só está completo se houver pelo menos 1 serviço
        if (stepId === 'services') {
            if (services.length === 0) {
                return false;
            }
        }
        // Para o passo 'page', só está completo se o campo about tiver informação
        if (stepId === 'page') {
            if (!establishment?.about || establishment.about.trim() === '') {
                return false;
            }
        }
        return !!completedSteps[stepId];
    }, [completedSteps, establishment?.cpf, establishment?.cnpj, establishment?.about, professionals, services]);

    // Conta quantos passos foram completados
    const completedCount = ONBOARDING_STEPS.filter((step) => isStepCompleted(step.id)).length;
    const totalSteps = ONBOARDING_STEPS.length;
    const allCompleted = completedCount === totalSteps;
    const progress = (completedCount / totalSteps) * 100;

    // Notifica o pai quando o estado de abertura mudar
    useEffect(() => {
        onOpenChange?.(open);
    }, [open, onOpenChange]);

    // Inicializa verificando diretamente o localStorage
    useEffect(() => {
        const dismissed = localStorage.getItem(STORAGE_KEY_DISMISSED);
        const isDismissed = dismissed === 'true';

        if (!isDismissed && autoOpen && !allCompleted) {
            setOpen(true);

            // Envia evento para o GTM - onboarding_step_view (visualização do onboarding)
            if (typeof window !== 'undefined' && window.dataLayer) {
                window.dataLayer.push({
                    event: 'onboarding_step_view',
                    step_number: completedCount + 1,
                    total_steps: ONBOARDING_STEPS.length,
                });
            }
        }
        setIsInitialized(true);
    }, [autoOpen, allCompleted, completedCount]);

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);

    // Só permite fechar se todos estiverem completos
    const handleClose = useCallback(() => {
        if (allCompleted) {
            setOpen(false);
        }
    }, [allCompleted]);

    // Marca um passo como completo (só marca, não faz toggle)
    const handleMarkAsComplete = useCallback(
        (stepId) => {
            setCompletedSteps({
                [stepId]: true,
            });
        },
        [setCompletedSteps]
    );

    // Clica no passo - navega ou copia link e marca como feito
    const handleStepClick = useCallback(
        (step, stepIndex) => {
            // Envia evento para o GTM - onboarding_next (avanço de etapa)
            if (typeof window !== 'undefined' && window.dataLayer) {
                window.dataLayer.push({
                    event: 'onboarding_next',
                    step_number: stepIndex + 1,
                    step_id: step.id,
                    step_title: step.title,
                    establishment_id: establishment?.id,
                });
            }

            // Marca o passo como concluído ao clicar
            setCompletedSteps({
                [step.id]: true,
            });

            // Ação especial para "Complete seus dados da conta"
            if (step.id === 'account') {
                setOpenCompleteProfileModal(true);
                return;
            }

            if (step.path) {
                // Fecha o drawer no mobile antes de navegar
                if (isMobile) {
                    setOpen(false);
                }
                router.push(step.path);
            } else {
                // Ação especial para copiar link (mesmo formato do "Compartilhar meu estabelecimento")
                const pageLink = `${window.location.origin}/establishment-page/${establishment?.id}`;
                navigator.clipboard.writeText(pageLink);
                toast.success('Link copiado para a área de transferência');
            }
        },
        [router, establishment?.id, setCompletedSteps, isMobile]
    );

    // Callback quando o perfil for completado com sucesso
    const handleProfileCompleted = useCallback(() => {
        setOpenCompleteProfileModal(false);
        // Marca o passo como concluído
        setCompletedSteps({ account: true });
    }, [setCompletedSteps]);

    // Verifica se deve mostrar o FAB (mobile, não completou tudo, não dispensou)
    const showFab = isMobile && !allCompleted && !onboardingDismissed;

    return (
        <>
            <CompleteProfileModal
                open={openCompleteProfileModal}
                onClose={() => setOpenCompleteProfileModal(false)}
                establishment={establishment}
                onSuccess={handleProfileCompleted}
            />

            {/* FAB flutuante para mobile */}
            {showFab && !open && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: theme.zIndex.speedDial,
                    }}
                >
                    <Badge
                        badgeContent={`${completedCount}/${totalSteps}`}
                        color="primary"
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                minWidth: 32,
                                height: 20,
                                borderRadius: 10,
                            },
                        }}
                    >
                        <Fab
                            color="primary"
                            onClick={handleOpen}
                            sx={{
                                width: 64,
                                height: 64,
                                boxShadow: theme.customShadows?.z16 || '0 8px 16px 0 rgba(0,0,0,0.24)',
                                position: 'relative',
                            }}
                        >
                            {/* Progresso circular ao redor do FAB */}
                            <CircularProgress
                                variant="determinate"
                                value={progress}
                                size={64}
                                thickness={3}
                                sx={{
                                    position: 'absolute',
                                    color: alpha(theme.palette.common.white, 0.3),
                                    '& .MuiCircularProgress-circle': {
                                        strokeLinecap: 'round',
                                    },
                                }}
                            />
                            <Iconify icon="solar:checklist-minimalistic-bold" width={28} />
                        </Fab>
                    </Badge>
                </Box>
            )}

            <Drawer
                open={open}
                onClose={() => setOpen(false)}
                anchor="right"
                variant={isMobile ? 'temporary' : 'persistent'}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: ONBOARDING_DRAWER_WIDTH },
                        boxShadow: (t) => t.customShadows?.z16 || '-12px 0 32px -4px rgba(0,0,0,0.12)',
                    }
                }}
            >
                <Scrollbar>
                    <Box sx={{ p: 3, pt: 4 }}>
                        {/* Header */}
                        <Box sx={{ mb: 4, textAlign: 'center' }}>
                            {/* Botão fechar no mobile */}
                            {isMobile && (
                                <IconButton
                                    onClick={() => setOpen(false)}
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 12,
                                        color: 'text.secondary',
                                    }}
                                >
                                    <Iconify icon="eva:close-fill" width={24} />
                                </IconButton>
                            )}

                            <Box
                                sx={{
                                    width: 64,
                                    height: 64,
                                    mx: 'auto',
                                    mb: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                }}
                            >
                                <Iconify
                                    icon="solar:checklist-minimalistic-bold"
                                    width={32}
                                    sx={{ color: 'primary.main' }}
                                />
                            </Box>

                            <Typography variant="h5" gutterBottom>
                                Primeiros Passos
                            </Typography>

                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                Complete essas etapas para configurar seu negócio
                            </Typography>

                            {/* Progress */}
                            <Box sx={{ px: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Progresso
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {completedCount}/{totalSteps}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 4,
                                        },
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Steps */}
                        <Stack spacing={2}>
                            {ONBOARDING_STEPS.map((step, index) => (
                                <StepButton
                                    key={step.id}
                                    step={step}
                                    index={index}
                                    isCompleted={isStepCompleted(step.id)}
                                    onMarkComplete={() => handleMarkAsComplete(step.id)}
                                    onClick={() => handleStepClick(step, index)}
                                />
                            ))}
                        </Stack>

                        {/* Complete Button - só aparece quando todos estão completos */}
                        {allCompleted && (
                            <Button
                                fullWidth
                                size="large"
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    // Envia evento para o GTM
                                    if (typeof window !== 'undefined' && window.dataLayer) {
                                        window.dataLayer.push({
                                            event: 'onboarding_completed',
                                            onboarding_steps_completed: completedCount,
                                            establishment_id: establishment?.id,
                                        });
                                    }
                                    setOnboardingDismissed(true);
                                    setOpen(false);
                                }}
                                startIcon={<Iconify icon="solar:check-circle-bold" />}
                                sx={{ mt: 4 }}
                            >
                                Tudo Pronto!
                            </Button>
                        )}

                        {/* Botão Pular - permite fechar temporariamente */}
                        {!allCompleted && (
                            <Button
                                fullWidth
                                size="small"
                                variant="text"
                                color="inherit"
                                onClick={() => {
                                    // Envia evento para o GTM - onboarding_skip (abandono)
                                    if (typeof window !== 'undefined' && window.dataLayer) {
                                        window.dataLayer.push({
                                            event: 'onboarding_skip',
                                            steps_completed: completedCount,
                                            total_steps: totalSteps,
                                            establishment_id: establishment?.id,
                                        });
                                    }
                                    // Apenas fecha o drawer, não salva no localStorage
                                    // Vai reaparecer no próximo reload
                                    setOpen(false);
                                }}
                                sx={{ mt: 3, color: 'text.disabled' }}
                            >
                                Pular por agora
                            </Button>
                        )}
                    </Box>
                </Scrollbar>
            </Drawer>
        </>
    );
}

// ----------------------------------------------------------------------

function StepButton({ step, index, isCompleted, onMarkComplete, onClick }) {
    const theme = useTheme();

    const handleItemClick = () => {
        onClick();
    };

    return (
        <Box
            onClick={handleItemClick}
            sx={{
                p: 2,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                border: `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
                bgcolor: isCompleted ? alpha(theme.palette.success.main, 0.08) : 'transparent',
                borderColor: isCompleted ? theme.palette.success.main : alpha(theme.palette.grey[500], 0.24),
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                    bgcolor: isCompleted
                        ? alpha(theme.palette.success.main, 0.12)
                        : alpha(theme.palette.grey[500], 0.08),
                },
            }}
        >
            {/* Checkbox */}
            <Button
                onClick={(e) => {
                    e.stopPropagation();
                    onMarkComplete();
                }}
                sx={{
                    minWidth: 40,
                    width: 40,
                    height: 40,
                    p: 0,
                    mr: 2,
                    borderRadius: '50%',
                    bgcolor: isCompleted ? theme.palette.success.main : alpha(theme.palette.grey[500], 0.08),
                    color: isCompleted ? 'white' : 'text.secondary',
                    border: isCompleted ? `2px solid ${theme.palette.success.main}` : 'none',
                    '&:hover': {
                        bgcolor: isCompleted
                            ? theme.palette.success.dark
                            : alpha(theme.palette.grey[500], 0.16),
                    },
                }}
            >
                {isCompleted ? (
                    <Iconify icon="eva:checkmark-fill" width={20} />
                ) : (
                    <Typography variant="subtitle2">{index + 1}</Typography>
                )}
            </Button>

            {/* Content */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        mb: 0.5,
                        textDecoration: isCompleted ? 'line-through' : 'none',
                        color: isCompleted ? 'text.disabled' : 'text.primary',
                    }}
                >
                    {step.title}
                </Typography>

                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        display: 'block',
                        whiteSpace: 'normal',
                    }}
                >
                    {step.description}
                </Typography>
            </Box>

            {/* Arrow */}
            <Iconify
                icon="eva:arrow-ios-forward-fill"
                width={20}
                sx={{
                    color: 'text.disabled',
                    flexShrink: 0,
                    ml: 1,
                }}
            />
        </Box>
    );
}

// ----------------------------------------------------------------------

// Exporta um botão trigger caso queira usar separadamente
export function OnboardingButton({ onClick, sx, ...other }) {
    return (
        <Button
            variant="soft"
            color="primary"
            startIcon={<Iconify icon="solar:checklist-minimalistic-bold" />}
            onClick={onClick}
            sx={sx}
            {...other}
        >
            Primeiros Passos
        </Button>
    );
}
